/**
 * Typed hooks for the report-templates endpoints, built on TanStack
 * Query. Every hook requires a live auth session; useApi() threads the
 * Bearer token.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client.js';
import type {
  ReportTemplateDefinition,
  ReportTemplateSummary,
  ReportTemplateVersion,
  ReportTemplateWithCurrent,
} from './types.js';

export function useReportTemplates() {
  const api = useApi();
  return useQuery<ReportTemplateSummary[]>({
    queryKey: ['report-templates'],
    queryFn: () => api.fetch('/report-templates'),
  });
}

/**
 * CSAGE-141: this one query opts OUT of the app-wide caching defaults.
 *
 * `main.tsx` sets `staleTime: 30_000` and `refetchOnWindowFocus: false` for
 * every query, which is right for the lists and wrong for this. A template's
 * `currentVersion` is edited by OTHER actors — an admin publishing via MCP, or
 * the same user in a second tab — and it decides which parameters the run form
 * renders. Under the shared defaults a user sitting on a Report instance kept
 * seeing the old version's parameter set until a hard refresh: publish a
 * version going from 3 params to 18, and the form still offers 3, with no
 * indication anything is stale.
 *
 * So: always re-fetch on mount and on window focus. `refetchOnMount: 'always'`
 * rather than plain `true` because `true` still respects `staleTime` — with a
 * 30s stale window inherited from the defaults, navigating away and back
 * inside that window would serve the cached version and reproduce the bug in
 * miniature.
 *
 * Scoped deliberately to this hook rather than loosened globally. The blast
 * radius of changing the defaults is every query in the app, and the report
 * template is the object with a genuine cross-actor write pattern.
 *
 * Not the full fix the ticket describes: a server-sent invalidation channel on
 * `publish_report_template` would update open clients immediately instead of
 * on their next mount or focus. This meets the stated acceptance (form updates
 * without a hard refresh, on next nav or focus) at a fraction of the cost.
 */
export function useReportTemplate(id: string | null) {
  const api = useApi();
  return useQuery<ReportTemplateWithCurrent>({
    queryKey: ['report-templates', id],
    queryFn: () => api.fetch(`/report-templates/${id}`),
    enabled: !!id,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
}

export function useReportTemplateVersions(id: string | null) {
  const api = useApi();
  return useQuery<ReportTemplateVersion[]>({
    queryKey: ['report-templates', id, 'versions'],
    queryFn: () => api.fetch(`/report-templates/${id}/versions`),
    enabled: !!id,
  });
}

export function useCreateReportTemplate() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; definition: ReportTemplateDefinition }) =>
      api.fetch<ReportTemplateWithCurrent>('/report-templates', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['report-templates'] });
    },
  });
}

export function usePublishVersion(templateId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    // Omit `definition` to publish the current draft (the typical path).
    // Pass `definition` to publish a specific payload, bypassing the draft.
    mutationFn: (input: { definition?: ReportTemplateDefinition } = {}) =>
      api.fetch<ReportTemplateWithCurrent>(`/report-templates/${templateId}/versions`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['report-templates'] });
      void qc.invalidateQueries({ queryKey: ['report-templates', templateId] });
      void qc.invalidateQueries({ queryKey: ['report-templates', templateId, 'versions'] });
    },
  });
}

export function useSaveTemplateDraft(templateId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { definition: ReportTemplateDefinition }) =>
      api.fetch<ReportTemplateWithCurrent>(`/report-templates/${templateId}/draft`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['report-templates', templateId] });
    },
  });
}

export function useUpdateTemplateSettings(templateId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: {
      name?: string;
      folderId?: string | null;
      audience?: 'internal' | 'customer' | 'partner' | 'regulator';
      pageOrientation?: 'portrait' | 'landscape';
      pageSize?: 'letter' | 'legal' | 'a4' | 'tabloid';
      pageMargins?: { top: number; right: number; bottom: number; left: number };
    }) =>
      api.fetch<ReportTemplateWithCurrent>(`/report-templates/${templateId}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['report-templates'] });
      void qc.invalidateQueries({ queryKey: ['report-templates', templateId] });
    },
  });
}

/** Permanently delete a template + its versions. Reports (instances)
 * that reference it cascade-delete via the FK. */
export function useDeleteTemplate() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (templateId: string) =>
      api.fetch<{ ok: true }>(`/report-templates/${templateId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['report-templates'] });
      void qc.invalidateQueries({ queryKey: ['reports'] });
    },
  });
}

export function useDiscardTemplateDraft(templateId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.fetch<ReportTemplateWithCurrent>(`/report-templates/${templateId}/draft/discard`, {
        method: 'POST',
        body: '{}',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['report-templates', templateId] });
    },
  });
}

export function useRollback(templateId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { toVersionId: string }) =>
      api.fetch<ReportTemplateWithCurrent>(`/report-templates/${templateId}/rollback`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['report-templates'] });
      void qc.invalidateQueries({ queryKey: ['report-templates', templateId] });
    },
  });
}
